import React from 'react';
import { useI18n } from '../../../contexts/I18nContext';
import { Pagination } from '../../common/Pagination';
import { FirmwareItem } from '../../../types/otaUpdate';
import { CopyUrlButton } from '../CopyUrlButton';
import { formatFileSize } from './otaData';
import { FirmwareActiveFilter, OtaOverviewState } from './useOtaOverview';

interface FirmwareTabProps {
  ota: OtaOverviewState;
  onOpen: (firmware: FirmwareItem) => void;
}

/** The firmware catalogue: filters, one row per firmware, paging. A row opens the firmware's dialog. */
export const FirmwareTab: React.FC<FirmwareTabProps> = ({ ota, onOpen }) => {
  const { formatDate, t } = useI18n();

  if (ota.firmwareItems.length === 0) {
    return (
      <div className="firmware-versions-list">
        <div className="empty-state">
          <div className="empty-icon">📦</div>
          <h3>{t('otaUpdates.noFirmwareVersions')}</h3>
          <p>{t('otaUpdates.noFirmwareHint')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="firmware-versions-list">
      <div className="firmware-filters" role="search" aria-label={t('otaUpdates.filtersLabel')}>
        <input
          type="text"
          data-testid="field-firmware-search"
          className="firmware-filter-search"
          placeholder={t('otaUpdates.filterSearchPlaceholder')}
          value={ota.search}
          onChange={(e) => ota.setSearch(e.target.value)}
          aria-label={t('otaUpdates.filterSearchPlaceholder')}
        />
        <select className="firmware-filter-select" value={ota.model} onChange={(e) => ota.setModel(e.target.value)} aria-label={t('otaUpdates.model')}>
          <option value="">{t('otaUpdates.filterAllModels')}</option>
          {ota.models.map((m) => <option key={m} value={m}>{m}</option>)}
        </select>
        <select className="firmware-filter-select" value={ota.type} onChange={(e) => ota.setType(e.target.value)} aria-label={t('otaUpdates.type')}>
          <option value="">{t('otaUpdates.filterAllTypes')}</option>
          {ota.types.map((typ) => <option key={typ} value={typ}>{typ}</option>)}
        </select>
        <select className="firmware-filter-select" value={ota.active} onChange={(e) => ota.setActive(e.target.value as FirmwareActiveFilter)} aria-label={t('otaUpdates.status')}>
          <option value="all">{t('otaUpdates.filterAllStatus')}</option>
          <option value="active">{t('otaUpdates.filterActiveOnly')}</option>
          <option value="inactive">{t('otaUpdates.filterInactiveOnly')}</option>
        </select>
        {ota.canSeeInactive && (
          <label className="filter-toggle-inactive" title={t('common.showInactiveHint')}>
            <input type="checkbox" checked={ota.showInactive} onChange={(e) => ota.setShowInactive(e.target.checked)} />
            {t('common.showInactive')}
          </label>
        )}
        <button type="button" className="firmware-filter-reset" onClick={ota.clearFilters} disabled={!ota.hasFilters}>
          {t('otaUpdates.filterReset')}
        </button>
        <span className="firmware-filter-count">
          {t('otaUpdates.filterCount', { shown: ota.filteredFirmware.length.toString(), total: ota.firmwareItems.length.toString() })}
        </span>
      </div>

      {ota.filteredFirmware.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">🔎</div>
          <p>{t('otaUpdates.filterNoMatches')}</p>
        </div>
      ) : (
        <table className="ota-table firmware-table">
          <thead>
            <tr>
              <th>{t('otaUpdates.version')}</th>
              <th>{t('otaUpdates.model')}</th>
              <th>{t('otaUpdates.type')}</th>
              <th>{t('otaUpdates.fromVersion')}</th>
              <th>{t('otaUpdates.fileUrl')}</th>
              <th>{t('otaUpdates.size')}</th>
              <th>{t('otaUpdates.status')}</th>
              <th>{t('otaUpdates.created')}</th>
            </tr>
          </thead>
          <tbody>
            {ota.filteredFirmware.map((fw) => (
              <tr key={fw.id} className="firmware-row clickable" onClick={() => onOpen(fw)}>
                <td><span className="version-cell">{fw.version}</span></td>
                <td><span className="model-cell">{fw.model}</span></td>
                <td><span className={`type-badge ${fw.type.toLowerCase()}`}>{fw.type}</span></td>
                <td><span className="from-version-cell">{fw.from_version || t('otaUpdates.anyVersionShort')}</span></td>
                <td>
                  <div className="url-table-cell" onClick={(e) => e.stopPropagation()}>
                    <span className="url-truncated" title={fw.file_url}>{fw.file_url ? `...${fw.file_url.slice(-30)}` : '-'}</span>
                    {fw.file_url && <CopyUrlButton url={fw.file_url} />}
                  </div>
                </td>
                <td><span className="size-cell">{formatFileSize(fw.file_size)}</span></td>
                <td>
                  <span className={`status-badge ${fw.is_active ? 'up-to-date' : 'pending'}`}>{fw.is_active ? t('common.active') : t('common.inactive')}</span>
                </td>
                <td><span className="date-cell">{formatDate(fw.created_at)}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <Pagination
        page={ota.page}
        totalPages={ota.totalPages}
        totalItems={ota.total}
        pageSize={ota.pageSize}
        itemLabel="firmware"
        onPageChange={ota.setPage}
        onPageSizeChange={(size) => { ota.setPageSize(size); ota.setPage(1); }}
      />
    </div>
  );
};
